/**
 * Turn per-store sweep timings into the one thing the health strip should say.
 *
 * The strip used to join every store's age with middle dots and let the row
 * truncate — unreadable at five stores, worse at nine, and it hid exactly the
 * store that had stopped. The fix is to report the exception: a sentence when
 * everything is fine, the failing store named when it is not, and the full
 * table only when asked for.
 */

export type SweepStore = {
	id: string;
	name: string;
	enabled: boolean;
	lastSweepAt: number | null;
	sweepIntervalMs: number;
	breakerOpen: boolean;
};

export type SweepRowState = "ok" | "needs" | "fault" | "off";

export type SweepRow = {
	id: string;
	name: string;
	state: SweepRowState;
	ago: string;
};

export type SweepHealth = {
	state: "ok" | "needs" | "fault";
	summary: string;
	rows: SweepRow[];
};

/**
 * How far past its own cadence a store may drift before it is called overdue.
 * Sweeps are skipped while one is already running and re-scheduled after
 * manual runs, so a store can legitimately land a little late; three missed
 * turns is not noise.
 */
const LATE_FACTOR = 3;

/** Compact age, matching the tone of the rest of the strip. */
export function fmtAge(ms: number): string {
	if (ms < 60_000) return `${Math.max(1, Math.floor(ms / 1000))}s`;
	if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m`;
	if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h`;
	return `${Math.floor(ms / 86_400_000)}d`;
}

function plural(n: number, one: string, many: string): string {
	return n === 1 ? one : many;
}

const RANK: Record<SweepRowState, number> = { fault: 0, needs: 1, ok: 2, off: 3 };

export function summariseSweeps(stores: SweepStore[], now: number = Date.now()): SweepHealth {
	const rows: SweepRow[] = stores.map((s) => {
		if (!s.enabled) {
			return { id: s.id, name: s.name, state: "off", ago: "disabled" };
		}
		const age = s.lastSweepAt == null ? null : now - s.lastSweepAt;
		const overdue = age == null || age > Math.max(s.sweepIntervalMs, 30_000) * LATE_FACTOR;
		return {
			id: s.id,
			name: s.name,
			state: s.breakerOpen ? "fault" : overdue ? "needs" : "ok",
			ago: age == null ? "never" : `${fmtAge(age)} ago`,
		};
	});
	// What needs attention first, then what is fine, then what is switched off.
	rows.sort((a, b) => RANK[a.state] - RANK[b.state] || a.name.localeCompare(b.name));

	const active = stores.filter((s) => s.enabled);
	if (active.length === 0) {
		return { state: "ok", summary: "No stores enabled", rows };
	}

	// A tripped breaker outranks lateness: it is usually the cause of it, and
	// it is the thing to act on.
	const down = active.filter((s) => s.breakerOpen);
	if (down.length > 0) {
		return {
			state: "fault",
			summary:
				down.length === 1
					? `${down[0]!.name} unreachable, requests paused`
					: `${down.length} stores unreachable, requests paused`,
			rows,
		};
	}

	const late = active.filter((s) => {
		const age = s.lastSweepAt == null ? null : now - s.lastSweepAt;
		return age == null || age > Math.max(s.sweepIntervalMs, 30_000) * LATE_FACTOR;
	});
	if (late.length === 1) {
		const s = late[0]!;
		const when = s.lastSweepAt == null ? "never swept" : `last swept ${fmtAge(now - s.lastSweepAt)} ago`;
		return {
			state: "needs",
			summary: `${s.name} ${when}, expects every ${fmtAge(s.sweepIntervalMs)}`,
			rows,
		};
	}
	if (late.length > 1) {
		const oldest = late.reduce((a, b) => ((a.lastSweepAt ?? 0) < (b.lastSweepAt ?? 0) ? a : b));
		const when = oldest.lastSweepAt == null ? "never swept" : `${fmtAge(now - oldest.lastSweepAt)} ago`;
		return {
			state: "needs",
			summary: `${late.length} stores overdue, oldest ${when}`,
			rows,
		};
	}

	// Everything inside its cadence: report the weakest link, not each store.
	const oldestAge = Math.max(...active.map((s) => (s.lastSweepAt == null ? 0 : now - s.lastSweepAt)));
	return {
		state: "ok",
		summary: `${plural(active.length, "1 store", `All ${active.length} stores`)} swept within ${fmtAge(oldestAge)}`,
		rows,
	};
}
